'use client';
import { plpSpecificObjects } from '@finn/auto-ui/components/CosmicHelperWrapper/pageSpecificObjects';
import { CosmicContent } from '@finn/auto-ui/components/PLPAdvisories';
import { useClosedUserGroup } from '@finn/auto-ui/contexts/ClosedUserGroup';
import { useGetUSPBanner } from '@finn/auto-ui/hooks/useGetUSPBanner';
import { useLoyaltyAndSubscriptionTracker } from '@finn/auto-ui/modules/checkout/components/Pages/UnifiedCheckout/CheckoutAside/useLoyaltyAndSubscriptionTracker';
import { FAKE_PRODUCTS } from '@finn/auto-ui/modules/products/fakeProducts';
import { RecommendationSection } from '@finn/auto-ui/modules/recommendation/components/RecommendationSection';
import CosmicModule from '@finn/auto-ui/ui-modules/CosmicModule';
import ProductSlider from '@finn/auto-ui/ui-modules/ProductSlider';
import { buildFAQJsonLdAsStrForPage } from '@finn/auto-ui/utils/html';
import { showToast } from '@finn/design-system/atoms/toast';
import {
  CosmicHelperWrapper,
  SpacingWrapper,
  useDeals,
} from '@finn/platform-modules';
import { BASE_SUBSCRIPTION_PATH } from '@finn/ua-constants';
import { Features, useIsABVariant } from '@finn/ua-featureflags';
import {
  AvailableFilters,
  CombinedFilters,
  CombinedFiltersTags,
  DiscoveryConfigProvider,
  FilterContext,
  FilterKey,
  FilterProvider,
  FILTERS_RESPONSE_SWR_FALLBACK_KEY,
  getPageSize,
  GetVehiclesResponse,
  PathFilterProvider,
  ProductGroupBy,
  ProductListing,
  TopCampaignBanner,
  useAvailableFiltersForState,
  useFiltersForProducts,
  useFilterValues,
  useIsOutOfStock,
  usePartnerDiscount,
  useProducts,
  useReplaceFilterValues,
  VehicleViewKey,
} from '@finn/ua-vehicle';
import { TopCampaignBannerData, USPBannerData } from '@finn/ui-cosmic';
import { cn, CookieKeys, useUniversalRouter } from '@finn/ui-utils';
import cookies from 'js-cookie';
import get from 'lodash/get';
import { useSearchParams } from 'next/navigation';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { SWRConfig } from 'swr';

import { AlertMessage } from '~/components/Alerts';
import ErrorPage from '~/components/ErrorPage';
import HeadTags from '~/components/HeadTags';
import MetaContent from '~/components/MetaContent';
import PartnerVoucherBanner from '~/components/PartnerVoucherBanner';
import useGetContentPieces from '~/components/ProductListingPage/useGetContentPieces';
import useOnPLPMount from '~/components/ProductListingPage/useOnPLPMount';
import Page from '~/layouts/Default';
import { FiltersTracking } from '~/modules/products/filters';
import { BaseLandingPage, SeoHeaderData } from '~/types/cosmicModules';
import { IErrorPage } from '~/types/general';
import { IPropsProductListingPage } from '~/types/productListing';
import { StickyNavContent } from '~/ui-modules/StickyNav/types';

interface IStickyNavContentProp {
  stickyNavContent?: StickyNavContent;
}

interface ICommonProps extends BaseLandingPage {
  plpLd?: string;
  productListItemsResponse?: GetVehiclesResponse;
  nextAvailableFilters: AvailableFilters;
  seoHeader: SeoHeaderData;
  renderMetaContent?: (
    pageData: BaseLandingPage['pageData'],
    { uspBanner }: { uspBanner: USPBannerData }
  ) => ReactNode;
}

export type Props = ICommonProps &
  IErrorPage &
  IPropsProductListingPage &
  IStickyNavContentProp;

// TODO temporary extracted in separate component
// to provide access to context, will be removed after context removed
const PLPCore = ({
  productListItemsResponse,
  pageData,
  isOutOfStock,
  topCampaignBanner,
  withTopSection,
}: Partial<ICommonProps> & {
  isOutOfStock?: boolean;
  topCampaignBanner: TopCampaignBannerData;
  withTopSection?: boolean;
}) => {
  const { closedUserGroupId } = useClosedUserGroup();
  const partnerDiscount = usePartnerDiscount({
    closedUserGroupId,
  });
  const gridRef = useRef<HTMLDivElement | null>(null);
  useLoyaltyAndSubscriptionTracker('Product Listing');
  const { deals: subscriptions } = useDeals();
  const filtersForProductsToFetch = useFiltersForProducts({
    subscriptions,
    initialFilters: isOutOfStock
      ? {
          [FilterKey.VIEW]: VehicleViewKey.DISPLAYED_CARS,
        }
      : undefined,
  });

  const { filters, products, status, total, limit, loadNextPage, page } =
    useProducts({
      fallbackData: productListItemsResponse,
      infinite: !isOutOfStock,
      limit: getPageSize(
        isOutOfStock,
        pageData.metadata.campaign_cards?.length
      ),
      gridRef,
      groupBy: ProductGroupBy.BRAND_MODEL,
      filters: filtersForProductsToFetch,
    });

  const searchParams = useSearchParams();
  const reason = searchParams.get('reason');
  const reasonType = searchParams.get('reason_type');
  const showedToast = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (reasonType && showedToast.current !== reasonType) {
      let title;
      if (reasonType === 'success') {
        title = 'Diese Ergebnisse gefunden';
      } else if (reasonType === 'not_found') {
        title = 'Entschuldigung, ich habe das Auto nicht gefunden';
      } else if (reasonType === 'wrong_prompt') {
        title = 'Tut mir leid, ich kann das Auto nicht finden';
      } else if (reasonType === 'no_results') {
        title = 'Tut mir leid, ich habe keine Ergebnisse gefunden';
      }

      showToast({
        title,
        description: reason,
        status: reasonType === 'success' ? 'success' : 'error',
        duration: 10000,
      });

      showedToast.current = reasonType;
    }
  }, [reasonType, reason]);

  const filterValues = useFilterValues();
  const availableFiltersDynamic = useAvailableFiltersForState(filterValues);
  const isOutOfStockDynamic = useIsOutOfStock();

  const replaceFilterValues = useReplaceFilterValues();
  const isExpCategories = useIsABVariant(Features.ExpPlpCategories);

  const productsWithFakes = useMemo(() => {
    if (!isExpCategories) {
      return products;
    }

    const hasFilters = Object.values(filterValues).filter(Boolean).length > 0;
    if (hasFilters) {
      return products;
    }

    if (cookies.get(CookieKeys.CATEGORY_FAKE_DOOR_CLOSED)) {
      return products;
    }

    const newProducts = [...products];
    for (const fakeProduct of FAKE_PRODUCTS) {
      if (fakeProduct.position < products.length) {
        newProducts.splice(fakeProduct.position, 1, fakeProduct.product);
      }
    }

    return newProducts;
  }, [products, filterValues, isExpCategories]);

  return (
    <div className="container">
      {withTopSection ? (
        <>
          <CombinedFiltersTags totalResults={total} />
          <TopCampaignBanner topCampaignBanner={topCampaignBanner} />
        </>
      ) : null}
      <div ref={gridRef} data-testid="product-listing">
        <RecommendationSection filters={filters} />
        <FiltersTracking loading={status} total={total} />
        <ProductListing
          CosmicModule={CosmicModule}
          status={status}
          partnerDiscount={partnerDiscount}
          campaignData={pageData.metadata.campaign_cards}
          items={productsWithFakes}
          paginationType={isOutOfStock ? 'pagination' : 'infinite'}
          page={page}
          total={total}
          limit={limit}
          fetchNextPage={loadNextPage}
          filterValues={filterValues}
          availableFilters={availableFiltersDynamic}
          isOutOfStock={isOutOfStockDynamic}
          onMessage={(type) => {
            if (type === 'reset_filters') {
              replaceFilterValues({});
            }
          }}
        />
      </div>
    </div>
  );
};

export const SeoRelevantPLP = ({
  plpLd,
  isOutOfStock,
  productListItemsResponse,
  nextAvailableFilters,
  allAvailableFilters,
  vehicles,
  pageData,
  initialFilter,
  seoHeader,
  totalVehiclesCount,
  headline,
  advisoryContentObject,
  statusCode,
  errorMessage,
  isMobile,
  renderMetaContent,
}: Props) => {
  const jsonLdArray = [];
  const jsonldAsString =
    advisoryContentObject &&
    buildFAQJsonLdAsStrForPage(advisoryContentObject.metadata.faq?.questions);

  if (jsonldAsString) {
    jsonLdArray.push(jsonldAsString);
  }
  if (plpLd) {
    jsonLdArray.push(plpLd);
  }

  const uspBanner = useGetUSPBanner({
    pageData,
    initialFilter,
    totalVehiclesCount,
    nextAvailableFilters,
    allAvailableFilters,
    shouldConsiderAsLandingPageProp: false,
  });

  const topCampaignBanner =
    pageData?.metadata?.promo_banners?.top_campaign_banner_test;

  const {
    isCarNotExist,
    isRedirectedPageNotExist,
    isDealClosed,
    isCkoUnauthorized,
  } = useOnPLPMount();

  const { alertMessage, labels } = useGetContentPieces({
    pageData,
    isRedirectedPageNotExist,
    isDealClosed,
    isCkoUnauthorized,
  });

  const swrConfigValue = useMemo(() => {
    return {
      fallback: {
        [FILTERS_RESPONSE_SWR_FALLBACK_KEY]: allAvailableFilters,
      },
    };
  }, [allAvailableFilters]);

  const { deals } = useDeals();
  const router = useUniversalRouter();

  if (statusCode) {
    return (
      <ErrorPage
        pageData={pageData}
        statusCode={statusCode}
        error={new Error(errorMessage)}
      />
    );
  }

  return (
    <SWRConfig value={swrConfigValue}>
      <PathFilterProvider
        basePathname={BASE_SUBSCRIPTION_PATH}
        isOutOfStock={isOutOfStock}
      >
        <CosmicHelperWrapper
          data={pageData}
          preview={false}
          localizationObjects={plpSpecificObjects}
        />
        {(isCarNotExist ||
          isRedirectedPageNotExist ||
          isDealClosed ||
          isCkoUnauthorized) && <AlertMessage message={alertMessage} />}
        <FilterProvider
          allAvailableFilters={allAvailableFilters}
          nextAvailableFilters={nextAvailableFilters}
          pageData={pageData}
          vehicles={vehicles}
          vehiclesOffset={productListItemsResponse.offset}
          labelsData={labels}
          totalVehiclesCount={totalVehiclesCount}
          advisoryContentObject={advisoryContentObject}
          seoHeader={seoHeader}
          headline={headline}
          initialFilter={initialFilter}
        >
          <DiscoveryConfigProvider
            router={router}
            deals={deals}
            filterOverrides={{
              power: {
                hidden: true,
              },
            }}
          >
            <Page data={pageData} isMobile={isMobile}>
              <FilterContext.Consumer>
                {() => (
                  <>
                    <HeadTags jsonLdContent={jsonLdArray} />
                  </>
                )}
              </FilterContext.Consumer>

              <div className="mt-4 md:mt-6">
                <div className="container md:flex">
                  <div
                    className={cn('w-full', {
                      '-mt-4 flex flex-col': !isOutOfStock,
                    })}
                  >
                    <PartnerVoucherBanner className="mb-2" />
                    <MetaContent
                      renderMetaContent={renderMetaContent}
                      pageData={pageData}
                      uspBanner={uspBanner}
                      isOutOfStock={isOutOfStock}
                    />
                    <div
                      className={cn({
                        'md:flex': !isOutOfStock,
                      })}
                    >
                      {!isOutOfStock ? (
                        <>
                          <CombinedFilters />
                          <PLPCore
                            withTopSection
                            isOutOfStock={isOutOfStock}
                            pageData={pageData}
                            productListItemsResponse={productListItemsResponse}
                            topCampaignBanner={topCampaignBanner}
                          />
                        </>
                      ) : (
                        <div className="container">
                          <PLPCore
                            withTopSection={false}
                            isOutOfStock={isOutOfStock}
                            pageData={pageData}
                            productListItemsResponse={productListItemsResponse}
                            topCampaignBanner={topCampaignBanner}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {isOutOfStock && (
                  <div className="container">
                    <SpacingWrapper />
                    <ProductSlider
                      title={get(
                        pageData,
                        'metadata.vehicles_slider_title',
                        ''
                      )}
                      isInPLP
                    />
                  </div>
                )}
                {advisoryContentObject && (
                  <CosmicContent
                    cosmicObject={advisoryContentObject}
                    nextAvailableFilters={nextAvailableFilters}
                  />
                )}
              </div>
            </Page>
          </DiscoveryConfigProvider>
        </FilterProvider>
      </PathFilterProvider>
    </SWRConfig>
  );
};
